import { appErrorAction } from '../actions/App';

const SYSTEM_ERROR = 'system error';
const BUSINESS_ERROR = 'business error';
const NOT_AVAILABLE = 'Not available';


/**
 * Throws a generic error that will cause the app to show generic error page.
 * @param  {object} errorData The data describing the error.
 * @param  {function} dispatch The action dispatch function from the store.
 */
export const throwGenericError = (errorData, dispatch) => {
  const error = {
    errorType: 'generic',
    errorData,
  };
  dispatch(appErrorAction(error));
};

export const throwCustomError = (errorData, dispatch) => {
  dispatch(appErrorAction(errorData));
};

export const createErrorBody = (errorMsg, ...errorData) => {
  return {
    message: errorMsg,
    errorData,
  };
};

const handle400Code1111 = (data, errorConfig, isCalculatingPrice, isPurchase) => {
  if (errorConfig[data.code]) {
    if (isCalculatingPrice && errorConfig[data.code].calcPriceOverride) {
      return errorConfig[data.code].calcPriceOverride;
    }
    if (isPurchase && errorConfig[data.code].purchaseOverride) {
      return errorConfig[data.code].purchaseOverride;
    }
    return errorConfig[data.code];
  }
  if (isCalculatingPrice && errorConfig.general.calcPriceOverride) {
    return errorConfig.general.calcPriceOverride;
  }
  if (isPurchase && errorConfig.general.purchaseOverride) {
    return errorConfig.general.purchaseOverride;
  }
  return errorConfig.general;
};

const handle400 = (data, errorConfig, isCalculatingPrice, isPurchase) => {
  // We'll only handle the first error.
  if (typeof data === 'object') {
    const { errors: [error] } = data;
    switch (error.code) {
      case 1111:
        return handle400Code1111(error.service, errorConfig, isCalculatingPrice, isPurchase);
      default:
        return errorConfig.general;
    }
  }
  return errorConfig.general;
};

const handle422 = (data, errorConfig) => {
  // We'll only handle the first error.
  if (typeof data === 'object') {
    const { errors: [error] } = data;
    switch (error.code) {
      default:
        return errorConfig.general;
    }
  }
  return errorConfig.general;
};

const handle277 = (data, errorConfig) => {
  // We'll only handle the first error.
  if (typeof data === 'object' && data.errors.length > 0) {
    try {
      return errorConfig[data.errors[0].code];
    } catch (e) {
      // do nothing
    }
  }
  return errorConfig.general;
};

const handle500 = (data, errorConfig) => {
  // We'll only handle the first error.
  if (data && data.errorDetails && data.errorDetails.errorCode) {
    try {
      return errorConfig[data.errorDetails.errorCode] || errorConfig.general;
    } catch (e) {
      return errorConfig.general;
    }
  }
  return errorConfig.general;
};

export const getErrorProps = (
  errorObject,
  errorConfig,
  debugServices,
  debugErrors,
  isCalculatingPrice,
  isPurchase
) => {
  if (!errorObject.response) {
    return errorConfig.general;
  }

  const { response: { status, data } } = errorObject;

  if (debugErrors) {
    console.warn(`Service failed with status: ${status}`, data.errors); /* eslint-disable-line */
  }

  switch (status) {
    case 400:
      return handle400(data, errorConfig, isCalculatingPrice, isPurchase);
    case 422:
      return handle422(data, errorConfig);
    case 277:
      return handle277(data, errorConfig);
    case 500:
      return handle500(data, errorConfig);
    default:
      return errorConfig.general;
  }
};

const getGenericErrorTracking = () => {
  return {
    'error_type': SYSTEM_ERROR,
    'error_code': NOT_AVAILABLE,
    'error_message': NOT_AVAILABLE,
  };
};

export const getErrorDetailsTrackingData = (errorDetails) => {
  return {
    'error_type': errorDetails.errorCode ? BUSINESS_ERROR : SYSTEM_ERROR,
    'error_code': errorDetails.errorCode ? errorDetails.errorCode : NOT_AVAILABLE,
    'error_message': errorDetails.errorText ? errorDetails.errorText : NOT_AVAILABLE,
  };
};

const getErrorTrackingData = (data) => {
  if (data.errorDetails) {
    return getErrorDetailsTrackingData(data.errorDetails);
  }

  if (data.errors) {
    const errors = data.errors.filter(error => error.service);
    // Return first service error.
    if (errors.length) {
      const error = errors[0];
      return {
        'error_type': error.service.type,
        'error_code': error.service.code,
        'error_message': error.service.message,
      };
    }
  }

  // Found no service error.
  return getGenericErrorTracking();
};

export const setErrorTracking = (errorObject) => {
  /* global Drupal */
  // Let track the error.
  let error;
  if (!errorObject.response) {
    error = getGenericErrorTracking();
  } else {
    const { response: { data } } = errorObject;
    error = getErrorTrackingData(data);
  }
  if (typeof Drupal !== 'undefined' && Drupal.behaviors && Drupal.behaviors.trygTealium) {
    // Let reset hash so the error will be sent.
    Drupal.behaviors.trygTealium.hash = 'error';
    Drupal.behaviors.trygTealium.flowError = error;
  }
};

export const returnCustomErrorCodeContent = (errorCode, listOfErrors) => {
  for(const element of listOfErrors){
    if(errorCode === element.errorCode){
      return element;
    }
  }
}

const handleCustomErrorUrl = (customErrorUrlData) => {
  // If "giveErrorIfMatch" is true, it will return with the initial result. However, if "giveErrorIfMatch" is false, it will return with the opposite.
  if(customErrorUrlData.searchFor === "part"){
    const part = window.location.href.includes(customErrorUrlData.value);
    const result = customErrorUrlData.giveErrorIfMatch ? part : !part;
    return {"errorCode": customErrorUrlData.errorCode, "result": result};
  } else {
    const full = customErrorUrlData.value === window.location.href;
    const result = customErrorUrlData.giveErrorIfMatch ? full : !full;
    return {"errorCode": customErrorUrlData.errorCode, "result": result};
  }
}

const handleCustomErrorUserData = (customErrorUserData, userData) => {
  let customErrorResponse = false;
  if(customErrorUserData.value in userData && !customErrorUserData.exactValue && !customErrorUserData.partValue) {
    customErrorResponse = true;
  }
  if (customErrorUserData.value in userData) {
    // Checks if exactValue exists and compares it to the value found in the userData. It checks if the value provided is somewhere to be found in the value from userData.
    if(customErrorUserData.partValue){
      customErrorResponse = userData[customErrorUserData.value].includes(customErrorUserData.partValue);
    }

    // Checks if exactValue exists and compares it to the value found in the userData. It checks for the exact value.
    if(customErrorUserData.exactValue !== undefined && customErrorUserData.exactValue !== null && customErrorUserData.exactValue === userData[customErrorUserData.value]){
      customErrorResponse = true;
    } else if(customErrorUserData.exactValue !== undefined && customErrorUserData.exactValue !== null && customErrorUserData.exactValue !== userData[customErrorUserData.value]){
      customErrorResponse = false;
    }

    // Checks if higherThanValue is higher than the specific value
    if(customErrorUserData.higherThanValue !== undefined && customErrorUserData.higherThanValue !== null && userData[customErrorUserData.value].length > 0){
      let customErrorUserDataValue = userData[customErrorUserData.value];
      if(typeof userData[customErrorUserData.value] != Number){
        customErrorUserDataValue = Number(userData[customErrorUserData.value]);
      }
      if(customErrorUserData.higherThanValue < customErrorUserDataValue) {
        customErrorResponse = true;
      } else {
        customErrorResponse = false;
      }
    } else if(customErrorUserData.higherThanValue !== undefined && customErrorUserData.higherThanValue !== null && userData[customErrorUserData.value].length === 0){
      customErrorResponse = false;
    }

    // Checks if lowerThanValue is higher than the specific value
    if(customErrorUserData.lowerThanValue !== undefined && customErrorUserData.lowerThanValue !== null && userData[customErrorUserData.value].length > 0){
      let customErrorUserDataValue = userData[customErrorUserData.value];
      if(typeof userData[customErrorUserData.value] != Number){
        customErrorUserDataValue = Number(userData[customErrorUserData.value]);
      }
      if(customErrorUserData.lowerThanValue > customErrorUserDataValue) {
        customErrorResponse = true;
      } else {
        customErrorResponse = false;
      }
    } else if(customErrorUserData.lowerThanValue !== undefined && customErrorUserData.lowerThanValue !== null && userData[customErrorUserData.value].length === 0){
      customErrorResponse = false;
    }

    customErrorResponse = customErrorUserData.giveErrorIfMatch ? customErrorResponse : !customErrorResponse;
  }
  return customErrorResponse;
}

export const identifyCustomError = (customErrors, userData) => {
  let customErrorCode;
  let customErrorResult = false;
  for(const element of customErrors){
    if (element.active) {
      switch (element.field) {
        case "url":
          if(handleCustomErrorUrl(element).result === true){
            customErrorResult = true;
            customErrorCode = element.errorCode;
          }
          break;
        case "userData":
          if(handleCustomErrorUserData(element, userData) === true){
            customErrorResult = true;
            customErrorCode = element.errorCode;
          }
          break;
        default:
          break;
      }
    }
  }
  return {"customErrorResult": customErrorResult, "customErrorCode": customErrorCode};
}